import { useRef } from 'react';
import { translate } from '@washed/i18n';
import { useReveal } from '../motion/useReveal.js';
import { Reveal } from '../motion/Reveal.js';
import { brandify } from '../components/Brand.js';
import styles from './Colophon.module.css';

export interface ColophonProps {
  locale: 'fr' | 'en';
}

export function Colophon({ locale }: ColophonProps) {
  const t = (k: Parameters<typeof translate>[0]) => translate(k, locale);
  const ref = useRef<HTMLElement>(null);
  const inView = useReveal(ref as React.RefObject<Element | null>, {
    threshold: 0.08,
    rootMargin: '0px 0px -20px 0px',
  });
  const en = locale === 'en';

  const serviceLinks = [
    {
      href: en ? '/en/comment-ca-marche' : '/comment-ca-marche',
      label: t('marketing.footer.col.service.link1'),
    },
    { href: en ? '/en/tarifs' : '/tarifs', label: t('marketing.footer.col.service.link2') },
    { href: en ? '/en/zones' : '/zones', label: t('marketing.footer.col.service.link3') },
    {
      href: en ? '/en/washerwoman-service-lome' : '/laveuse-a-domicile-lome',
      label: en ? 'In-home laundry guide' : 'Guide laveuse à domicile',
    },
  ] as const;

  const washedLinks = [
    {
      href: en ? '/en/a-propos' : '/a-propos',
      label: t('marketing.footer.col.washed.link2'),
    },
    {
      href: en ? '/en/devenir-laveuse' : '/devenir-laveuse',
      label: t('marketing.footer.col.washed.link1'),
    },
    {
      href: en ? '/en/lome-laundry-research' : '/etude-lessive-lome',
      label: en ? 'Lomé laundry research' : 'Étude lessive à Lomé',
    },
    {
      href: 'mailto:contact@washedafrica.com?subject=Presse%20Washed',
      label: t('marketing.footer.col.washed.link3'),
    },
    {
      href: en ? '/en/contact' : '/contact',
      label: t('marketing.footer.col.washed.link4'),
    },
  ] as const;

  const mentionsLinks = [
    {
      href: en ? '/privacy' : '/confidentialite',
      label: t('marketing.footer.col.mentions.link1'),
    },
    { href: en ? '/terms' : '/conditions', label: t('marketing.footer.col.mentions.link2') },
    {
      href: en ? '/data-deletion' : '/suppression-donnees',
      label: t('marketing.footer.col.mentions.link3'),
    },
  ] as const;

  const homeHref = en ? '/en' : '/';

  return (
    <footer
      ref={ref}
      className={`${styles.colophon} ${inView ? styles.in : ''}`}
      role="contentinfo"
    >
      <div className={styles.top}>
        <div className={styles.brandBloc}>
          <a
            href={homeHref}
            className={styles.wm}
            aria-label={locale === 'fr' ? 'Washed, accueil' : 'Washed, home'}
          >
            washed<span className={styles.wmDot}>.</span>
          </a>
          <p>{brandify(t('marketing.footer.colophon'))}</p>
        </div>
        <div>
          <p className={styles.h5}>{t('marketing.footer.col.service.h5')}</p>
          {serviceLinks.map((link) => (
            <a href={link.href} key={link.href}>
              {link.label}
            </a>
          ))}
        </div>
        <div>
          <p className={styles.h5}>{brandify(t('marketing.footer.col.washed.h5'))}</p>
          {washedLinks.map((link) => (
            <a href={link.href} key={link.href}>
              {link.label}
            </a>
          ))}
        </div>
        <div>
          <p className={styles.h5}>{t('marketing.footer.col.mentions.h5')}</p>
          {mentionsLinks.map((link) => (
            <a href={link.href} key={link.href}>
              {link.label}
            </a>
          ))}
        </div>
      </div>
      <div className={styles.bottom}>
        <span>{t('marketing.footer.copyright')}</span>
      </div>
    </footer>
  );
}
